import {
  buildRichTooltip,
  RICH_TOOLTIP_CLASS,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../rich-tooltips"

export const SCHEDULING_ACTION_TOOLTIP_CLASS = RICH_TOOLTIP_CLASS

type SchedulingTooltipParts = {
  title: string
  action: string
  effect?: string
  hint?: string
  prerequisite?: string
}

const actionTooltip = ({
  title,
  action,
  effect,
  hint,
  prerequisite,
}: SchedulingTooltipParts): string =>
  buildRichTooltip([
    tooltipTitle(title),
    tooltipField("Was passiert", action),
    tooltipField("Auswirkung", effect),
    tooltipField("Voraussetzung", prerequisite),
    tooltipText(hint),
  ])

export const schedulingActionTooltips = {
  calendarCreateAppointment: tooltipText(
    "Einen Auftragstermin planen. Urlaub und andere Abwesenheiten legen Sie über „Blocker anlegen“ an.",
  )!,
  calendarCreatePastDayUnavailable: tooltipText(
    "Bitte einen aktuellen oder zukünftigen Tag auswählen.",
  )!,
  calendarPrint: tooltipText(
    "Druckt die aktuelle Kalenderansicht mit gewähltem Zeitraum und Mitarbeitern.",
  )!,
  dayReschedule: actionTooltip({
    title: "Termine dieses Tages verschieben",
    action:
      "Wählen Sie die Termine dieses Tages aus, die Sie verschieben möchten, und planen Sie anschließend Ersatztermine.",
    effect:
      "KehrBlatt storniert die ausgewählten Termine, plant Ersatztermine und kann Kunden vorab informieren.",
  }),
  dayRescheduleUnavailable: actionTooltip({
    title: "Keine verschiebbaren Termine",
    action:
      "In dieser Spalte gibt es aktuell keine Termine, die gesammelt verschoben werden können.",
    effect: "Bereits erledigte, bestätigte oder fachlich gesperrte Termine bleiben unverändert.",
  }),
  eventSave: tooltipText("Änderungen am Kalendereintrag speichern.")!,
  eventConfirm: actionTooltip({
    title: "Termin bestätigen",
    action: "Als bestätigt markieren, wenn der Kunde dem Termin zugestimmt hat.",
    effect:
      "Der Termin gilt danach als fest vereinbart und kann von Kunden nicht mehr wie ein offener Vorschlag bestätigt werden.",
    hint: "Nutzen Sie das, wenn der Termin intern oder mit dem Kunden verbindlich geklärt wurde.",
  }),
  eventReserve: actionTooltip({
    title: "Termin reservieren",
    action: "Wandelt einen theoretischen Termin in eine Reservierung um.",
    effect:
      "Der Zeitraum wird im Kalender blockiert, bevor der Termin endgültig bestätigt oder weiterbearbeitet wird.",
  }),
  eventReschedule: tooltipText("Einen neuen Termin auswählen, der den bisherigen ersetzt.")!,
  eventDelete: actionTooltip({
    title: "Termin oder Eintrag löschen",
    action: "Löscht diesen Termin oder Kalendereintrag aus KehrBlatt.",
    effect:
      "Falls der Eintrag zu einem Auftrag gehört, müssen Termin und Kundeninformation bei Bedarf neu geplant werden.",
    hint: "Diese Aktion ist nur für löschbare Einträge verfügbar.",
  }),
  eventNoShow: actionTooltip({
    title: "Nicht stattgefunden",
    action: "Markiert einen bereits erledigten Termin als nicht stattgefunden.",
    effect: "Der zugehörige Auftrag wird wieder geöffnet und kann neu geplant werden.",
  }),
  eventContactOrder: actionTooltip({
    title: "Ankündigungsplan erstellen",
    action: "Startet oder öffnet den Ankündigungsplan für diesen Termin.",
    effect: "KehrBlatt bereitet die für den Tarif verfügbaren Kontaktwege vor.",
  }),
  scheduleBatchReschedule: actionTooltip({
    title: "Termine verschieben",
    action: "Öffnet die Sammelverschiebung für die ausgewählten Termine.",
    effect:
      "Nur verschiebbare Termine werden verarbeitet; nicht passende Zeilen bleiben unverändert.",
  }),
  scheduleRowReschedule: tooltipText("Einen neuen Termin auswählen, der den bisherigen ersetzt.")!,
  scheduleRowCancel: actionTooltip({
    title: "Termin stornieren",
    action: "Storniert diesen Termin im Kalender.",
    effect: "Der Auftrag bleibt erhalten und kann anschließend neu geplant oder geklärt werden.",
  }),
  cancelReplanCancelEvents: actionTooltip({
    title: "Termine stornieren und weiter",
    action: "Storniert die ausgewählten Termine als ersten Schritt der Sammelverschiebung.",
    effect: "Danach können betroffene Kunden informiert und Ersatztermine geplant werden.",
  }),
  cancelReplanCancelAnnouncements: actionTooltip({
    title: "Ankündigungspläne abbrechen",
    action: "Bricht aktive Ankündigungspläne der betroffenen Aufträge ab.",
    effect: "Noch nicht ausgeführte Kontaktschritte werden nicht weiter gestartet.",
    hint: "Nutzen Sie das, wenn die noch geplanten Kundenmitteilungen nicht mehr versendet werden sollen.",
  }),
  cancelReplanCompleteAnnouncements: actionTooltip({
    title: "Ankündigungspläne abschließen",
    action: "Markiert aktive Ankündigungspläne der betroffenen Aufträge als abgeschlossen.",
    effect:
      "Offene Kontaktschritte werden nicht weiter gestartet; der Plan gilt als manuell erledigt.",
    hint: "Nutzen Sie das, wenn die Kundeninformation außerhalb des automatischen Plans abgearbeitet wurde.",
  }),
} as const
